import apiPath from "../api-path";
import config from "../config";
import instanceGPT from "./auth-gpt.service";
import ResumeCheckerService from "./resume-checker/resume-checker.service";
import axios from "axios";


const getAllResume = () => {
    return instanceGPT.get(apiPath.gpt_interview + apiPath.resume);
}

const postResumeChecker = (formData: FormData | { job_title: string; job_description: string; resume_key: string }) => {
    // Use the new ResumeCheckerService that calls Firebase Functions
    const resumeCheckerService = ResumeCheckerService();
    return resumeCheckerService.postResumeChecker(formData);
}

// Separate function to parse resume only (returns parsed data)
const parseResumeFile = (resumeFile: File | Blob, fileName: string) => {
    if (!resumeFile || !fileName) {
        return Promise.reject(new Error("Cannot parse resume: File is required"));
    }
    
    if (!(resumeFile instanceof Blob)) {
        return Promise.reject(new Error("Cannot parse resume: Invalid file type"));
    }
    
    try {
        const formData = new FormData();
        formData.append("file", resumeFile, fileName);
        
        if (!formData.has("file")) {
            return Promise.reject(new Error("Cannot parse resume: Failed to create FormData"));
        }
        
        return resumeParser(formData)
            .then((parserResponse) => {
                console.log("Resume parsed successfully:", parserResponse.data);
                return parserResponse;
            })
            .catch((parserError) => {
                console.error("Resume parser failed:", parserError);
                return Promise.reject(parserError);
            });
    } catch (formDataError: any) {
        console.error("Error creating FormData:", formDataError);
        const errorMessage = formDataError?.message || formDataError?.toString() || "Unknown error";
        return Promise.reject(new Error(`Cannot parse resume: ${errorMessage}`));
    }
};

// Function to create resume after parsing (called after user confirms in popup)
const createResumeAfterParsing = (resume_name: string, tempalte_id: string, resume_key: string) => {
    const resumeJson = {
        "resume_name": resume_name,
        "template_id": tempalte_id,
        "resume_key": resume_key
    };
    return instanceGPT.post(apiPath.gpt_interview + apiPath.resume, resumeJson);
};

const addResumeWithResumeKey = (resume_name, tempalte_id, resume_key, resumeFile?: File | Blob, fileName?: string) => {
    const resumeJson = {
        "resume_name": resume_name,
        "template_id":tempalte_id,
        "resume_key" : resume_key
    }
    
    // Call resume-parser API first (before calling one-profile/profile/resume)
    // Always send file, not resume_key
    const parseResumePromise = (() => {
        // Must have file to send
        if (!resumeFile || !fileName) {
            return Promise.reject(new Error("Cannot parse resume: File is required"));
        }
        
        if (!(resumeFile instanceof Blob)) {
            return Promise.reject(new Error("Cannot parse resume: Invalid file type"));
        }
        
        try {
            const formData = new FormData();
            // Use any field name - busboy will accept any file field
            // The backend expects req.file, not a specific field name
            formData.append("file", resumeFile, fileName);
            
            // Verify FormData has the file
            if (!formData.has("file")) {
                return Promise.reject(new Error("Cannot parse resume: Failed to create FormData"));
            }
            
            // Call resume-parser API with file upload only (no fallback to resume_key)
            return resumeParser(formData)
                .then((parserResponse) => {
                    console.log("Resume parsed successfully:", parserResponse.data);
                    return parserResponse;
                })
                .catch((parserError) => {
                    console.error("Resume parser failed:", parserError);
                    return Promise.reject(parserError);
                });
        } catch (formDataError: any) {
            console.error("Error creating FormData:", formDataError);
            const errorMessage = formDataError?.message || formDataError?.toString() || "Unknown error";
            return Promise.reject(new Error(`Cannot parse resume: ${errorMessage}`));
        }
    })();
    
    // Call resume-parser first, then call one-profile/profile/resume only if parsing succeeds
    return parseResumePromise
        .then((parseResponse) => {
            // Only call one-profile/profile/resume if resume-parser succeeded
            if (parseResponse && parseResponse.data) {
                // After resume-parser completes successfully, call one-profile/profile/resume
                return instanceGPT.post(apiPath.gpt_interview + apiPath.resume, resumeJson);
            } else {
                // If parsing returned null/empty data, reject
                return Promise.reject(new Error("Resume parsing failed: No data returned from parser"));
            }
        })
        .catch((parseError) => {
            // If resume-parser fails, reject and don't call one-profile/profile/resume
            const errorMessage = parseError?.response?.data?.message || parseError?.message || parseError?.toString() || "Unknown error";
            console.error("Resume parsing failed, aborting resume creation:", errorMessage);
            return Promise.reject(new Error(`Resume parsing failed: ${errorMessage}`));
        });
}


const resumeParser = (objectResult: { resume_key?: string; resume_url?: string; resume_image_base64?: string; resume_image_mime_type?: string } | FormData) => {
    // Only use Firebase Functions to ensure OPEN_AI_API_KEY stays on backend
    const baseUrl = config.functions.base_url;
    if (!baseUrl) {
        return Promise.reject(new Error("REACT_APP_ONE_PROFILE_FUNCTIONS_URL is not configured. Resume parser requires Firebase Functions."));
    }
    
    // Check if objectResult is FormData (file upload) or plain object
    const isFormData = objectResult instanceof FormData;
    
    if (isFormData) {
        // If it's FormData (has file), use multipart/form-data
        // Don't set Content-Type header - axios will set it automatically with boundary
        return axios.post(
            `${baseUrl}/api/ai/resume-parser`,
            objectResult
        ).then(res => res).catch(err => {
            console.error("Error calling resume-parser API:", err);
            return Promise.reject(err);
        });
    } else {
        // If it's plain object (resume_key, resume_url, etc.), use JSON
        return axios.post(
            `${baseUrl}/api/ai/resume-parser`,
            objectResult,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        ).then(res => res).catch(err => {
            console.error("Error calling resume-parser API:", err);
            return Promise.reject(err);
        });
    }
}

const GPTService = {
    getAllResume,
    postResumeChecker,
    resumeParser,
    addResumeWithResumeKey,
    parseResumeFile,
    createResumeAfterParsing
};

export default GPTService;